import React from "react";
import cn from "classnames";
import {
  ButtonColour, Typography, TypographyVariants, ButtonSize,
} from "@trussle/external-components";
import { IContentfulBackgroundColour } from "../../../../types/IContentfulBackgroundColour";
import { IContentfulRichTextResponse } from "../../../../types/IContentfulRichText";
import { ICTA } from "../../../../types/ICTA";
import { addTracking, CallToActions } from "../../../CallToActions";
import { ReplaceQueryParams } from "../../../ReplaceQueryParams";
import { RichText } from "../../../RichText";
import { generateId } from "../../../../helpers/generateId";
import { HubSpotForm } from "../../../HubSpot/HubSpotForm";

import { ISplashPanelMediaProps, SplashPanelMedia } from "./media";
import { transformRichText } from "../../../../helpers/richText";
import "./styles.scss";
import { ISplashPanelPresets, ISplashPanelVariants } from "../../../../types/common";
import { ITickCrossItem, TickCrossList } from "./tickCrossList";
import { PanelWrapper } from "../../../PanelWrapper";

export interface ISplashPanelProps extends ISplashPanelMediaProps {
  alignment?: "left" | "center";
  backgroundColour?: IContentfulBackgroundColour;
  mainContent?: IContentfulRichTextResponse;
  ctas?: ICTA[];
  disclaimer?: IContentfulRichTextResponse;
  title?: string;
  headingType?: string;
  headerAnchor?: string;
  mediaAlignment?: string;
  mediaWidth?: string;
  variant?: ISplashPanelVariants;
  tickCrossList?: {
    items: ITickCrossItem[];
  },
  preset?: ISplashPanelPresets;
  betterV2?: boolean;
  snapScroll?: boolean;
}

export const OriginalSplashPanel: React.FunctionComponent<ISplashPanelProps> = ({
  index,
  alignment = "left",
  backgroundColour,
  mainContent,
  ctas,
  disclaimer,
  title,
  image,
  imageBorderRadius,
  youTubeVideoId,
  lookerId,
  hubSpotPortalId,
  hubSpotFormId,
  saasquatchTenantAlias,
  saasquatchWidgetType,
  headerAnchor,
  infogramId,
  headingType,
  mediaAlignment = "middle",
  mediaWidth = "half",
  tickCrossList,
  betterV2,
  snapScroll,
}) => {
  const contentRichTxt = mainContent && transformRichText(mainContent.raw);
  const disclaimerRichTxt = disclaimer && transformRichText(disclaimer.raw);
  const verticalAlignmentClass = `splashPanel_vertical-align_${mediaAlignment}`;
  const containerWidthClass = cn(
    {
      "splashPanel_width-half":
        (typeof mediaWidth === "object" && !mediaWidth)
        || mediaWidth === "half",
    },
    {
      "splashPanel_width-full": mediaWidth === "full",
    },
  );

  return (
    <PanelWrapper
      backgroundColour={!betterV2 ? backgroundColour : undefined}
      isBetterV2={betterV2}
      className={cn({
        "splashPanel-content-betterV2": betterV2,
        "splashPanel-content-betterV2--snap-scroll": betterV2 && snapScroll,
      })}
    >
      <div
        id={headerAnchor}
        className={cn(
          "splashPanel",
          !betterV2 ? `splashPanel--background-${backgroundColour}` : undefined,
          {
            "splashPanel-betterV2": betterV2,
          },
        )}
      >
        <div
          className={`splashPanel__container ${containerWidthClass} ${verticalAlignmentClass}`}
        >
          <div
            className={`splashPanel--content splashPanel--content-${alignment}`}
          >
            {mediaAlignment !== "bottom" && (
              <SplashPanelMedia
                youTubeVideoId={youTubeVideoId}
                lookerId={lookerId}
                hubSpotFormId={hubSpotFormId}
                hubSpotPortalId={hubSpotPortalId}
                index={index}
                infogramId={infogramId}
                image={image}
                imageBorderRadius={imageBorderRadius}
                saasquatchTenantAlias={saasquatchTenantAlias}
                saasquatchWidgetType={saasquatchWidgetType}
                betterV2={betterV2}
              />
            )}
            <div
              className="splashPanel__main"
              role="region"
              aria-label="Splash panel"
            >
              {title && (
                <Typography
                  classes="splashPanel__title"
                  variant={headingType as TypographyVariants || "h2"}
                  {...(title && {
                    id: generateId(title),
                  })}
                >
                  <ReplaceQueryParams>{title}</ReplaceQueryParams>
                </Typography>
              )}
              <div className="styledRichTextContainer">
                <RichText
                  contrastText={backgroundColour === "navy"}
                  content={contentRichTxt}
                />
              </div>
              <TickCrossList tickCrossList={tickCrossList?.items} className="splashPanel__tickCrossList__afterRichText" />
              {ctas && (
                <div className={`splashPanel--ctas-${alignment}`}>
                  <CallToActions
                    alignment={alignment}
                    centerMobileTablet
                    primaryCtaColour={ButtonColour.Primary}
                    secondaryCtaColour={backgroundColour === "grey" ? ButtonColour.TertiaryLight : ButtonColour.Secondary}
                    ctas={addTracking("Splash", ...ctas)}
                    size={ButtonSize.Large}
                  />
                </div>
              )}
              {disclaimerRichTxt && (
                <Typography variant="body3" classes="disclaimer_RichText">
                  <RichText
                    contrastText={backgroundColour === "navy"}
                    className="splashPanel__disclaimer rich-text-small"
                    content={disclaimerRichTxt}
                  />
                </Typography>
              )}
              {hubSpotPortalId && hubSpotFormId && index && (youTubeVideoId || lookerId || infogramId || image) && (
                <HubSpotForm hubSpotPortalId={hubSpotPortalId} hubSpotFormId={hubSpotFormId} index={index} />
              )}
            </div>
            {mediaAlignment === "bottom" && (
              <>
                <SplashPanelMedia
                  youTubeVideoId={youTubeVideoId}
                  lookerId={lookerId}
                  hubSpotFormId={hubSpotFormId}
                  hubSpotPortalId={hubSpotPortalId}
                  index={index}
                  infogramId={infogramId}
                  image={image}
                  imageBorderRadius={imageBorderRadius}
                  saasquatchTenantAlias={saasquatchTenantAlias}
                  saasquatchWidgetType={saasquatchWidgetType}
                  betterV2={betterV2}
                />
                <TickCrossList tickCrossList={tickCrossList?.items} className="splashPanel__tickCrossList__afterMedia" />
              </>
            )}
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
};
